<template>
    <div>
        <div v-if="show === 'login'">
            <h1 class="mb-1.5 mt-14">Welcome!</h1>
            <p class="mb-4 text-sm text-ds-primary-500">
                {{ titleName }} uses {{ appName }} as a secure portal for your real estate transaction to easily share information
                with you.
            </p>
        </div>
        <div v-if="show === 'login'">
            <div class="my-3">
                <div class="mb-1 text-content-primary">Email Address</div>
                <DsTextInput
                    v-model="loginForm.email"
                    data-testid="input-email"
                    type="text"
                    @blur="validateEmail"
                />
                <span
                    v-if="emailError"
                    class="text-ds-red-600"
                >
                    Please enter a valid email address
                </span>
                <span
                    v-if="submitError"
                    class="text-ds-red-600"
                >
                    We were unable to locate this account</span
                >
            </div>
            <div class="my-3 flex items-start gap-2">
                <DsCheckbox
                    v-model="loginForm.tos"
                    data-testid="checkbox-terms"
                    name="tos"
                    value="tos"
                >
                    <template #label>
                        <span class="text-sm text-ds-primary-500">
                            I agree to the
                            <a
                                :href="termsLink"
                                rel="noopener noreferrer"
                                target="_blank"
                            >
                                Terms of Service
                            </a>
                            and
                            <a
                                :href="privacyLink"
                                rel="noopener noreferrer"
                                target="_blank"
                            >
                                Privacy Policy
                            </a>
                        </span>
                    </template>
                </DsCheckbox>
            </div>
            <div class="my-3">
                <DsCheckbox
                    v-model="loginForm.rememberMe"
                    data-testid="checkbox-remember"
                    name="rememberDevice"
                    value="rememberDevice"
                >
                    <template #label>
                        <span class="text-sm text-ds-primary-500">Remember this device for 30 days</span>
                    </template>
                </DsCheckbox>
            </div>
            <div class="my-10">
                <button
                    class="text-uppercase block w-full rounded bg-ds-primary-900 px-4 py-2 text-center font-medium text-white"
                    :class="
                        !loginForm.tos || emailError || !loginForm.email
                            ? 'opacity-40'
                            : 'hover:bg-semantic-primary-hover focus:outline-none focus:ring-2 focus:ring-semantic-primary-hover focus:ring-offset-2'
                    "
                    data-testid="btn-submit"
                    :disabled="!loginForm.tos || emailError || !loginForm.email"
                    type="button"
                    @click="submitAction"
                >
                    {{ submitText }}
                </button>
            </div>
        </div>
        <EmailVerificationComponent
            v-if="show === 'verification'"
            @verificationCodeValid="haveVerificationCode"
        />
        <ClientPhoneNumberCollectionComponent
            v-if="show === 'phoneNumberCollection'"
            @phoneNumberCollected="handlePhoneNumberCollected"
        />
        <TwoFAFormComponent
            v-if="twoFANumber && show === 'passcode'"
            :client-phone-type="phoneType"
            :include-back-button="showBackButtonInMfa"
            :phone-number="twoFANumber"
            @backButtonPressed="handleBackFromMfa"
            @passcodeSent="ampTrack2FADeliveryMethod"
            @passcodeValid="havePasscode"
        />
        <div v-if="show === 'passcode' || show === 'verification' || show === 'phoneNumberCollection'">
            <hr class="text-ds-primary-200" />
            <p class="mt-3 text-xs text-ds-primary-500">Having issues? Please contact {{ titleName }}</p>
        </div>
        <div v-else>
            <hr class="text-ds-primary-200" />
            <p class="mt-3 text-xs text-content-secondary">
                This portal provides a secure way to send payment instructions, bank information, eSignatures, documents, etc.
                between you and {{ titleName }}. If you have any questions, please contact {{ titleName }} using independently
                verified contact information.
            </p>
            <p class="mt-4 text-xs font-bold text-content-secondary">Do not trust phone numbers in emails.</p>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useClientApi } from '@/api/client/api-client';
import type { PhoneType, TwilioPhoneType } from '@/api/interfaces/twilio';
import { notifyError } from '@/bugsnag';
import DsCheckbox from '@/components/design-system/form/input/DsCheckbox.vue';
import DsTextInput from '@/components/design-system/form/input/text/DsTextInput.vue';
import { datadogStartRUM, datadogStopRUM } from '@/shared/utils/datadog';
import type { OnFetchErrorContext } from '@vueuse/core';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import ClientPhoneNumberCollectionComponent from './ClientPhoneNumberCollectionComponent.vue';
import EmailVerificationComponent from './EmailVerificationComponent.vue';
import TwoFAFormComponent from './TwoFAFormComponent.vue';

const props = withDefaults(
    defineProps<{
        twoFactorAuth?: boolean;
        titleId: number;
        titleName: string;
        appName: string;
        email?: string;
        termsLink?: string;
        privacyLink?: string;
        clientPhoneType: TwilioPhoneType;
    }>(),
    {
        twoFactorAuth: false,
        email: '',
        termsLink: '',
        privacyLink: '',
    },
);

const emailError = ref(false);
const submitError = ref(false);
const in2FA = ref(false);
const loginForm = ref({
    email: props.email,
    tos: false,
    rememberMe: false,
});
const mfa = ref({
    verificationCode: {
        need: false,
        sent: false,
        have: false,
    },
    passcode: {
        need: false,
        sent: false,
        have: false,
    },
});
const show = ref('login');
const twoFANumber = ref<string | null>(null);
const phoneType = ref(props.clientPhoneType);
const showBackButtonInMfa = ref(false);

const submitText = computed(() => {
    return props.twoFactorAuth ? 'Continue' : 'Log In';
});

const clientApi = useClientApi();

async function submitAction() {
    ampTrackEmailLoggedIn();

    const { data, error } = await clientApi('/client/login/checkCredentials')
        .post({
            email: loginForm.value.email,
            terms: loginForm.value.tos,
            title_id: props.titleId,
        })
        .json();

    if (error.value) {
        submitError.value = true;
    } else {
        mfa.value.verificationCode.need = data.value['require_verification_code'];
        mfa.value.passcode.need = data.value['require_passcode'];
        twoFANumber.value = data.value['phoneNumber'];
        phoneType.value = data.value['phoneType'];
        next2FA();
    }
}

async function sendVerificationCode() {
    const { error } = await clientApi('/client/login/sendVerificationCode', {
        onFetchError(ctx: OnFetchErrorContext) {
            /**
             * Here, `onFetchError` is intercepting the response data and setting `error` to `data`
             * because the error messages are found in `context.data`, not `context.error`.
             * Note that in `ClientLoginController.php`, these error messages are sent as json.
             * */
            const error = ctx.data;
            ctx.error = error;

            return ctx;
        },
    }).post();
    if (error.value) {
        if (error.value !== 'Missing values in session') {
            notifyError(error.value, 'LoginFormComponent:sendVerificationCode', {
                response: error.value,
            });
        }
        console.error(error.value);
    } else {
        mfa.value.verificationCode.sent = true;
    }
}

async function login() {
    const { data, error } = await clientApi('/client/login', {
        onFetchError(ctx: OnFetchErrorContext) {
            /**
             * Here, `onFetchError` is intercepting the response data and setting `error` to `data`
             * because the error messages are found in `context.data`, not `context.error`.
             * Note that in `ClientLoginController.php`, these error messages are sent as json.
             * */
            const error = ctx.data;
            ctx.error = error;

            return ctx;
        },
    })
        .post({
            email: loginForm.value.email,
            remember: loginForm.value.rememberMe,
            terms: loginForm.value.tos,
            title_id: props.titleId,
        })
        .json();

    if (error.value) {
        const isExpectedError = [
            'Missing values in session',
            'verification code not validated',
            'passcode not validated',
        ].includes(error.value);

        if (!isExpectedError) {
            notifyError(error.value, 'LoginFormComponent:login', {
                response: error.value,
            });
        }

        console.error(error.value);
    } else {
        ampTrackLogin();
        window.location.href = data.value;
    }
}

const haveVerificationCode = () => {
    mfa.value.verificationCode.have = true;
    next2FA();
    ampTrackMfaCode();
};

const havePasscode = (phoneType: PhoneType) => {
    mfa.value.passcode.have = true;
    next2FA();
    ampTrack2FACode(phoneType);
};

const handlePhoneNumberCollected = (phoneNumber: string, phoneTypeValue: string) => {
    twoFANumber.value = phoneNumber;
    phoneType.value = phoneTypeValue as TwilioPhoneType;
    showBackButtonInMfa.value = true;
    next2FA();
};

const handleBackFromMfa = () => {
    show.value = 'phoneNumberCollection';
    twoFANumber.value = null;
};

const next2FA = () => {
    in2FA.value = true;
    if (mfa.value.verificationCode.need && !mfa.value.verificationCode.have) {
        show.value = 'verification';
        sendVerificationCode();
    } else if (mfa.value.passcode.need && !mfa.value.passcode.have) {
        if (!twoFANumber.value) {
            show.value = 'phoneNumberCollection';
        } else {
            show.value = 'passcode';
        }
    } else {
        showBackButtonInMfa.value = false;
        login();
    }
};

const validateEmail = () => {
    if (loginForm.value.email !== '') {
        emailError.value = !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(loginForm.value.email);
    }
};

//#region :: event tracking
const ampTrackEmailLoggedIn = () => {
    let eventProps = {
        prefilled: loginForm.value.email.length > 0,
        'terms selected': loginForm.value.tos,
        'remember device': loginForm.value.rememberMe,
    };
    ampAddCommonProps(eventProps);
    window.amplitude?.track('login - email populated', eventProps);
};

const ampInitialTrack = () => {
    let eventProps = {
        referrer: show.value === 'login' ? 'email' : 'text',
    };
    ampAddCommonProps(eventProps);
    window.amplitude?.track('login - open app', eventProps);
};

const ampTrackMfaCode = () => {
    let eventProps = {
        'mfa code': mfa.value.verificationCode.need && !mfa.value.verificationCode.have ? 'email' : 'text',
    };
    ampAddCommonProps(eventProps);
    window.amplitude?.track('login - mfa code populated', eventProps);
};

const ampTrack2FADeliveryMethod = (phoneType: PhoneType) => {
    let eventProps = {
        'delivery method': phoneType === 'sms' ? 'text' : 'call',
    };
    ampAddCommonProps(eventProps);
    window.amplitude?.track('login - 2FA delivery method populated', eventProps);
};

const ampTrack2FACode = (phoneType: PhoneType) => {
    let eventProps = {
        'delivery method': phoneType === 'sms' ? 'text' : 'call',
    };
    ampAddCommonProps(eventProps);
    window.amplitude?.track('login - 2FA code populated', eventProps);
};

const ampTrackLogin = () => {
    let eventProps = {};
    ampAddCommonProps(eventProps);
    window.amplitude?.track('login - completed', eventProps);
};

const ampAddCommonProps = (props: Record<string, unknown>) => {
    props['portal type'] = 'client';
};
//#endregion

// reset amplitude when the user logs out
window.amplitude?.reset();

document.querySelector('[page-loading]')?.remove();

onMounted(() => {
    if (loginForm.value.email) {
        window.amplitude?.setUserId(loginForm.value.email);
    }
    datadogStartRUM(loginForm.value.email);
    ampInitialTrack();
});

onBeforeUnmount(() => {
    datadogStopRUM();
});
</script>
